/**
 * estimateMark — the shared "geschätzt" chip (#2760 / #2779 / #2780).
 *
 * Scope, stated honestly: this module owns the chip used by the ranked TABLE. The drawer's
 * cascade line, Ø-Erlös strip and "Leistung ist eine Schätzung" note still carry their own
 * literals in ``RowDetail.tsx`` / ``sourceBreakdown.ts`` (including a second, independent
 * ``deckungsbeitrag-breakdown__badge`` chip). Folding those in would be a pure win — but
 * it is not done yet, so do NOT assume a wording change here reaches every surface.
 *
 * #2695 D can put a five-figure MODELLED Leistung on a row whose buyer has paid
 * nothing: when no revenue is booked, the materialiser values the cycle from the
 * curated sale price book and stamps ``leistung_estimated``. Every surface that says so
 * says it from here — one module, so a new surface cannot drift into its own wording or
 * its own pill.
 *
 * ``EstimatedChip`` is ``RankedMarginTable``'s ``EstimatedLeistungBadge``, lifted verbatim
 * (plus a ``betrieb`` grain for the since-retired hero). The table renders identically —
 * its existing tests are the regression gate.
 *
 * The module's other half — the ranked chart's 45° hatch (``EstimateHatch``,
 * ``estimateHatchId``, ``HATCH_WASH_OPACITY``, ``EstimateBarState``,
 * ``ESTIMATED_BAR_CAPTION``) and the #3056/#3089 contrast suite that measured it — went
 * with the chart on 2026-09-07; see frontend/ARCH.md § "the ranked chart is retired".
 */
import './estimateMark.css';

/** The chip word. One string, every grain — the long explanation rides in ``title``. */
export const ESTIMATED_LABEL = 'geschätzt';

/**
 * The grains need copy of DIFFERENT strength, and the difference is not cosmetic: each
 * ``title`` may assert only what the flag behind it actually evaluated.
 *
 * A Schlag leaf IS one cycle, so "für diesen Schlag ist kein Erlös gebucht" is exact.
 * A Kultur group's flag is ``some(...)`` over its member cycles, so its € is a BLEND of
 * booked revenue and estimate: claiming "für diese Kultur ist kein Erlös gebucht" would be
 * a plain falsehood about the booked half — the very confusion this marker exists to
 * remove, reintroduced by copy that over-claims. (PR #2752's review called that a bug.)
 *
 * #2809 — the Betrieb total reintroduced exactly that one level up, and the mis-premise was
 * in this docstring: the hero flag was NOT ``some(...)`` over Kulturen. ``marginHeaderSummary``
 * set ``totalDb1Estimated`` from ``visibleRows.filter(row => row.leistung_estimated)``
 * (``summary.ts`` — both deleted by HOF-20 along with the hero that consumed the ``betrieb``
 * grain; the grain itself stays, pinned by ``__tests__/estimateMark.test.ts:14–35``, because
 * unpicking that copy is its own change), and ``visibleRows`` were per-Schlag
 * ``DeckungsbeitragRow``s — #2695 D
 * estimates per CYCLE, so no Kultur-grain predicate is evaluated anywhere. One estimated
 * Schlag under a Kultur whose sibling Schlag booked 12.000 € therefore sets the hero flag
 * while no Kultur on the farm is without booked revenue, and "für mindestens eine Kultur"
 * is then flatly false. The Betrieb title asserts at SCHLAG grain for that reason, which
 * makes it word-for-word the Kultur one — both are ``some(...)`` over the same cycles and
 * differ only in what they aggregate. Two literals, deliberately not an alias: should one
 * grain's wording ever diverge, that must be a local edit rather than a silent drag.
 *
 * Rejected (#2809): re-basing the hero flag on ``rollupByKultur`` to make "mindestens eine
 * Kultur" true. It cannot, in either form it could take. Taken literally it is a NO-OP:
 * ``rollupByKultur`` folds the flag as ``members.some((m) => m.leistung_estimated)``
 * (``deckungsbeitragRollup.ts``), so "∃ group carrying the flag" is the same proposition as
 * "∃ row carrying it" — and the sentence stays just as false, because a flagged group can
 * still hold a Schlag that booked revenue. Making the Kultur claim TRUE would need a
 * different predicate: a Kultur *entirely* without booked revenue, i.e. ``every(...)`` over
 * its Schläge. That inverts the quantifier, and the farm above — one estimated Schlag, one
 * booked sibling — would then render the hero completely UNMARKED. Under-marking is the
 * product failure this whole family of issues exists to prevent, so the claim moves DOWN to
 * the grain the flag already evaluates instead.
 */
export const ESTIMATED_TITLE = {
  schlag: 'Leistung aus dem kuratierten Preisbuch geschätzt — für diesen Schlag ist kein Erlös gebucht.',
  kultur:
    'Enthält eine geschätzte Leistung aus dem kuratierten Preisbuch — für mindestens einen Schlag ist kein Erlös gebucht.',
  betrieb:
    'Enthält eine geschätzte Leistung aus dem kuratierten Preisbuch — für mindestens einen Schlag ist kein Erlös gebucht.',
  // HOF-195 — the Hauptschlag analysis unit. ``leistung_estimated`` folds with ``some()``
  // at this grain too, so the sentence hedges over the unit's BLOCKS, exactly as the
  // Kultur one hedges over its Schläge.
  einheit:
    'Enthält eine geschätzte Leistung aus dem kuratierten Preisbuch — für mindestens einen Block dieser Analyseeinheit ist kein Erlös gebucht.',
} as const;

/** Which identity the marked € aggregates: one Schlag, one Kultur+Sorte group, or the whole Betrieb. */
export type EstimateGrain = keyof typeof ESTIMATED_TITLE;

/**
 * The quiet pill beside a € that is NOT booked revenue. Deliberately no
 * ``aria-describedby``: the visible word carries the signal and ``title`` carries the
 * explanation, matching every other hint in this app (codex declined the ARIA
 * indirection on the table badge in PR #2752).
 */
export function EstimatedChip({ grain }: { grain: EstimateGrain }) {
  return (
    <>
      {' '}
      <span className="deckungsbeitrag-estimate-chip" title={ESTIMATED_TITLE[grain]}>
        {ESTIMATED_LABEL}
      </span>
    </>
  );
}
